import type { CSSProperties } from "react";

import type { OpenAPI } from "simplydo/interfaces";
import type { ChallengeWithIdeaTemplate } from "simplydo/interfaces/types/challengeTypes";
import type {
  IdeaAssessmentDetails as BaseIdeaAssessmentDetails,
  IdeaWithAssessments as BaseIdeaWithAssessments,
  IdeaWithOwnerChallenge,
  IdeaWithVotes,
} from "simplydo/interfaces/types/ideaTypes";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { UiColor } from "simplydo/react/theme";
import type { ButtonSize } from "simplydo/react/ui/button";

export type { IdeaWithOwnerChallenge, IdeaWithVotes };

export type IdeaAssessmentDetails = BaseIdeaAssessmentDetails;

export type IdeaWithAssessments = BaseIdeaWithAssessments & {
  invitedExternalAssessors?: string[];
};

export type LibIdeaDetail = OpenAPI.GET<"/ideas/{id}">["response"];

export type LibIdeaChallenge = ChallengeWithIdeaTemplate;

export type LibIdeaOwnerChallenge = NonNullable<
  OpenAPI.Schemas["ResponseGetIdea"]["ownerChallenge"]
>;

export type LibIdeaTag = NonNullable<
  NonNullable<OpenAPI.Schemas["ideaOwnerTags"]["ownerTags"]>[number]
>;

export type LibIdeaWithTags = IdeaWithOwnerChallenge & {
  ownerTags?: LibIdeaTag[];
};

export type LibIdeaWithIdeaTemplate = IdeaWithOwnerChallenge & {
  ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  templated?: Record<string, string | number | boolean | null | undefined>;
};

export type LibIdeaWithAssessments = IdeaWithAssessments & {
  invitedExternalAssessors?: string[];
  assessments?: { ownerUser?: OpenAPI.Schemas["User"] }[];
};

export type LibIdeaViewIdea = LibIdeaWithTags &
  LibIdeaWithIdeaTemplate &
  LibIdeaWithAssessments & {
    ownerChallenge?: LibIdeaOwnerChallenge;
    owner?: OpenAPI.Schemas["User"];
    projectManagement?: OpenAPI.Schemas["Idea"]["projectManagement"] & {
      boards?: LibIdeaProjectBoardEntry[];
    };
    assessmentDetails?: IdeaAssessmentDetails;
    votedFor?: boolean;
    voteCount?: number;
    completeness?: number;
    activityInfo?: { lastActivityAt?: string };
    children?: unknown;
    labels?: string[];
    isSubmitted?: boolean;
    isPinned?: boolean;
    authors?: OpenAPI.Schemas["User"][];
    /** Set once a "basic" idea has been enriched with full content */
    _enriched?: boolean;
    assessmentSummaries?: OpenAPI.Schemas["ResponseGetIdea"]["assessmentSummaries"];
    ownerStatus?: {
      _id: string;
      name: string;
      isTerminal?: boolean;
      color?: UiColor;
    };
    statusMessage?: string;
    statusAssignedAt?: string;
  };

export type LibIdeaProjectBoardEntry = NonNullable<
  NonNullable<OpenAPI.Schemas["Idea"]["projectManagement"]>["boards"]
>[number] & { laneObject?: { name?: string; laneCount?: number } };

export type LibIdeaViewChallenge = ChallengeWithIdeaTemplate & {
  tagList?: NonNullable<OpenAPI.Schemas["ideaTagList"]["tagList"]>;
  voteLimit?: string | number;
};

export type IdeaAssessment = NonNullable<
  NonNullable<IdeaWithAssessments["assessments"]>[number]
>;

export type IdeaDisplayField = {
  _id?: string;
  id?: string;
  name?: string;
  type?: string;
  title?: string;
  value?: unknown;
};

export type IdeaCompanyVerificationReport =
  OpenAPI.Schemas["CreditsafeReport"] & {
    tempReportUrl?: string;
    reportVisible?: boolean;
    reportUrl?: string;
    report?: string;
  };

export type IdeaTeamStartChatProps = {
  idea: IdeaWithOwnerChallenge;
  onMessageThreadSelected?: (threadId: string) => void;
  style?: CSSProperties;
  size?: ButtonSize;
  fluid?: boolean;
  basic?: boolean;
};

export type IdeaChallengeHelpProps = { challengeId: string };

export type IdeaCompanyVerificationProps = {
  idea: IdeaWithOwnerChallenge;
  report: IdeaCompanyVerificationReport;
  updateReportUrl: (url: string) => void;
  field: IdeaDisplayField;
};

export type IdeaCardViewProps = {
  idea: LibIdeaViewIdea;
  challenge: LibIdeaViewChallenge;
  onSelect?: (idea: OpenAPI.Schemas["Idea"]) => void;
  selectedIdeas?: string[];
  ideasVotedFor: number;
  setPreviewOpen?: (ideaId: string, options?: { toComments?: boolean }) => void;
};

export type IdeaAssessmentReportChallenge = ChallengeWithIdeaTemplate;

export type IdeaImpactsCache =
  OpenAPI.GET<"/challenges/{id}/ideas/impacts">["response"];

export type IdeaImpactsEntry = NonNullable<
  IdeaImpactsCache["ideaImpacts"][string]
>;

type IdeaTotalImpactItem = NonNullable<
  NonNullable<IdeaImpactsEntry["totalImpacts"]>
>[number];

export type IdeaImpactMeasure = IdeaTotalImpactItem;

export type ImpactStatementWithCreator = OpenAPI.Schemas["ImpactStatement"] & {
  ownerCreatedBy?: OpenAPI.Schemas["User"];
};

export type OrganisationImpact = OpenAPI.Schemas["OrganisationImpact"];

export type OrganisationImpactCategory =
  OpenAPI.Schemas["OrganisationImpactCategory"];

export type MappedImpactCategory = {
  _id?: string;
  name: string;
  order: number;
  impacts?: OrganisationImpact[];
  visible?: boolean;
};

export type ImpactStatementImpactsMap = NonNullable<
  ImpactStatementWithCreator["impacts"]
>;

export type ImpactStatementImpactValue =
  ImpactStatementImpactsMap[keyof ImpactStatementImpactsMap];

export type IdeaImpactDisplayMeasure = IdeaImpactMeasure & {
  ownerImpact?: OrganisationImpact;
  ownerImpactCategory?: { name?: string };
  annualisedValue?: string | number;
  value?: string | number;
  enabled?: boolean;
  isPinned?: boolean;
  impactId?: string;
  choice?: ImpactStatementImpactValue["choice"] & { toRange?: string };
  frequency?: ImpactStatementImpactValue["frequency"];
  justification?: string;
};

export type StatementImpactEntry = ImpactStatementImpactValue;

export type CreditsafeCreateReportResponse =
  OpenAPI.POST<"/verifications/companies/reports">["response"] & {
    report?: OpenAPI.POST<"/verifications/companies/reports">["response"]["report"] & {
      tempReportUrl?: string;
    };
  };

export type CreditsafeReportResponse =
  CreditsafeCreateReportResponse["report"] & { tempReportUrl?: string };

export type DropdownFieldValue =
  | string
  | number
  | boolean
  | (string | number | boolean)[]
  | undefined;

export const dropdownStringValue = (
  value: DropdownFieldValue,
): string | undefined => {
  if (value === undefined || value === null || Array.isArray(value)) {
    return undefined;
  }
  return String(value);
};

export type IdeasLibUser = UserMe | null;

export type RequestJoinInvitationBody = {
  forType: "requestJoinIdea";
  forId: string;
  invitee: string;
  note?: string;
};

type OpenAPIInvitationCreateBody = NonNullable<
  OpenAPI.POST<"/invitations">["requestBody"]
>;

/** OpenAPI POST /invitations omits requestJoinIdea from forType enum. */
export const toInvitationCreateBody = (
  body: RequestJoinInvitationBody,
): OpenAPIInvitationCreateBody => {
  // @ts-expect-error requestJoinIdea is valid at runtime but omitted from OpenAPI POST enum
  return body;
};

export type AssessmentReportSection = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]
>;

export type AssessmentReportField = NonNullable<
  NonNullable<AssessmentReportSection["fields"]>[number]
>;

export type AssessmentFieldScore = { score?: number; notes?: string };

export type IdeaAssessmentInvitation = OpenAPI.Schemas["Invitation"] & {
  inviteeUser?: OpenAPI.Schemas["User"];
  ideaDetails?: Record<string, { assessmentRounds?: string[] }>;
  profile?: { firstName?: string; created?: string };
};
